import { useState, useEffect, useMemo } from 'react';
import { useAdminI18n } from '@/lib/i18n/use-admin-i18n';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { PaginationState } from '@tanstack/react-table';
import type { TenantDto, User } from '@cms/models';
import { DynamicTable } from '@/components/custom/table/DynamicTable';
import type {
  DynamicTableColumnConfig,
  DynamicTableControllers,
} from '@/components/custom/table/dynamic-table-types';
import { STATUS_OPTIONS } from '@cms/utils';
import { SearchFilter, StatusFilter } from '@/components/custom/filters';
import { saasApi } from '@/lib/api/saas.api';
import { tenantUserApi } from '@/lib/api/tenant-user.api';
import { useAuthStore } from '@/stores/authStore';
import { TenantUserFormModal, type TenantUserFormModalMode } from './TenantUserFormModal';
import { TenantUserChangePasswordModal } from './TenantUserChangePasswordModal';
import {
  AdminTableRowActions,
  PagePrimaryActionButton,
} from '@/components/common/AdminTableRowActions';

type TenantUsersStatusFilter = (typeof STATUS_OPTIONS)[number]['value'] | '';

function syntheticTenantForSelfService(tenantId: string): TenantDto {
  const now = new Date().toISOString();
  return {
    id: tenantId,
    code: 'tenant',
    name: 'Your organization',
    status: 'active',
    url: undefined,
    notes: undefined,
    contact: undefined,
    createdAt: now,
    updatedAt: now,
  };
}

interface TenantUsersTableProps {
  rows: User[];
  controllers: DynamicTableControllers;
  rowOffset: number;
  onView: (user: User) => void;
  onEdit: (user: User) => void;
  onDelete: (user: User) => void;
  onChangePassword: (user: User) => void;
  isDeleting: boolean;
  isLoading: boolean;
  emptyMessage: string;
  /** When listing across tenants, show which tenant each row belongs to. */
  showTenantColumn: boolean;
  tenantLabelById: ReadonlyMap<string, string>;
}

function TenantUsersTable(props: Readonly<TenantUsersTableProps>) {
  const {
    rows,
    controllers,
    rowOffset,
    onView,
    onEdit,
    onDelete,
    onChangePassword,
    isDeleting,
    isLoading,
    emptyMessage,
    showTenantColumn,
    tenantLabelById,
  } = props;
  const { col, act, statusOptions, tableLoading } = useAdminI18n();

  const columns: DynamicTableColumnConfig<User>[] = useMemo(
    () => [
    {
      id: 'no',
      header: col('no'),
      columnType: 'number',
      cell: ({ index }) => rowOffset + index + 1,
      widthMode: 'fit',
      size: 48,
      meta: { className: 'text-muted-foreground' },
    },
    {
      id: 'email',
      header: col('email'),
      accessorKey: 'email',
      columnType: 'text',
      sortable: true,
      cell: ({ value }) => (
        <span className="font-mono text-xs text-foreground">{value as string}</span>
      ),
    },
    {
      id: 'name',
      header: col('name'),
      accessorKey: 'name',
      columnType: 'text',
      sortable: true,
      cell: ({ value }) => (value != null ? String(value) : '—'),
    },
    ...(showTenantColumn
      ? ([
          {
            id: 'tenant',
            header: col('tenant'),
            columnType: 'text' as const,
            cell: ({ row }) => {
              const tid = row.tenantId?.trim();
              if (!tid) return <span className="text-muted-foreground">—</span>;
              const label = tenantLabelById.get(tid) ?? tid;
              return <span className="text-muted-foreground">{label}</span>;
            },
          },
        ] satisfies DynamicTableColumnConfig<User>[])
      : []),
    {
      id: 'role',
      header: col('role'),
      columnType: 'text',
      cell: ({ row }) => (
        <span className="text-muted-foreground">{row.role?.name ?? '—'}</span>
      ),
    },
    {
      id: 'status',
      header: col('status'),
      accessorKey: 'status',
      columnType: 'badge',
      displayLabelOptions: statusOptions,
      widthMode: 'fit',
      size: 100,
      meta: { className: 'min-w-[100px]' },
    },
    {
      id: 'actions',
      header: col('actions'),
      columnType: 'actions',
      cell: ({ row }) => (
        <AdminTableRowActions
          act={act}
          onView={() => onView(row)}
          onEdit={() => onEdit(row)}
          onPassword={() => onChangePassword(row)}
          onDelete={() => onDelete(row)}
          isDeleting={isDeleting}
        />
      ),
      widthMode: 'fit',
      size: 220,
      minSize: 180,
      meta: { className: 'min-w-[180px] text-right' },
    },
    ],
    [act, col, isDeleting, onDelete, onEdit, onView, onChangePassword, rowOffset, showTenantColumn, statusOptions, tenantLabelById],
  );

  return (
    <DynamicTable<User>
      features={{
        columns: true,
        rows: true,
        sorting: true,
        pagination: true,
        globalFilter: false,
        stripedRows: true,
        fontSize: 'xs',
        compactPadding: true,
      }}
      columns={columns}
      rows={{
        data: rows,
        getRowId: row => row.id,
      }}
      controllers={controllers}
      isLoading={isLoading}
      loadingMessage={tableLoading}
      emptyMessage={emptyMessage}
    />
  );
}

export function TenantUsersPage() {
  const { col, page, statusOptions, allStatusesOption, tableLoading } = useAdminI18n();
  const user = useAuthStore(s => s.user);
  const isPlatformUser = Boolean(user?.isPlatformUser);
  const tenantId = useAuthStore(s => s.tenantId ?? null);
  const queryClient = useQueryClient();
  /** Platform admins: default empty = all tenant users (any tenant). Do not seed from profile tenantId. */
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<TenantUsersStatusFilter>('');
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });
  const [formModal, setFormModal] = useState<{
    open: boolean;
    mode: TenantUserFormModalMode;
    user: User | null;
  }>({ open: false, mode: 'create', user: null });
  const [changePasswordModal, setChangePasswordModal] = useState<{
    open: boolean;
    user: User | null;
  }>({ open: false, user: null });

  const { data: platformTenants = [] } = useQuery({
    queryKey: ['tenants'],
    queryFn: () => saasApi.getTenants(),
    enabled: isPlatformUser,
  });

  const tenants = useMemo((): TenantDto[] => {
    if (isPlatformUser) return platformTenants;
    const tid = tenantId?.trim();
    if (!tid) return [];
    return [syntheticTenantForSelfService(tid)];
  }, [isPlatformUser, platformTenants, tenantId]);

  // Tenant-scoped accounts: sync filter to JWT/profile tenant when it hydrates.
  useEffect(() => {
    if (isPlatformUser) return;
    setSelectedTenantId(tenantId ?? '');
  }, [isPlatformUser, tenantId]);

  const listTenantId = isPlatformUser
    ? selectedTenantId.trim() || undefined
    : tenantId ?? undefined;

  const tenantLabelById = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of tenants) {
      const id = t.id?.trim();
      if (id) m.set(id, (t.name?.trim() || t.code?.trim() || id) as string);
    }
    return m;
  }, [tenants]);

  const showTenantColumn = isPlatformUser && listTenantId === undefined;

  const { data: tenantUsersResponse, isLoading } = useQuery({
    queryKey: [
      'tenant-users',
      isPlatformUser ? selectedTenantId || '__all__' : tenantId ?? '',
      pagination.pageIndex + 1,
      pagination.pageSize,
      search,
      statusFilter,
    ],
    queryFn: () =>
      tenantUserApi.getList({
        tenantId: listTenantId,
        page: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
        search: search || undefined,
        status: statusFilter || undefined,
      }),
    enabled: isPlatformUser || Boolean(tenantId),
  });

  const rows = (tenantUsersResponse?.data ?? []) as User[];
  const total = tenantUsersResponse?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(Math.max(1, total) / pagination.pageSize));

  const deleteMutation = useMutation({
    mutationFn: (id: string) => tenantUserApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-users'] });
    },
  });

  const handleDelete = async (user: User) => {
    if (!globalThis.confirm(`Are you sure you want to delete user "${user.email}"?`)) {
      return;
    }
    try {
      await deleteMutation.mutateAsync(user.id);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      alert(err.response?.data?.message ?? 'Failed to delete user');
    }
  };

  const controllers: DynamicTableControllers = {
    pagination: {
      state: pagination,
      pageCount,
      rowCount: total,
      onPaginationChange: updater => {
        setPagination(prev => (typeof updater === 'function' ? updater(prev) : updater));
      },
    },
  };

  const rowOffset = pagination.pageIndex * pagination.pageSize;

  const listReady = isPlatformUser || Boolean(tenantId);
  const emptyMessage = !listReady
    ? tableLoading
    : showTenantColumn
      ? page('tenant_users', 'empty_all')
      : page('tenant_users', 'empty_tenant');

  return (
    <div className="space-y-2 p-2">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold">{page('tenant_users', 'title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{page('tenant_users', 'subtitle')}</p>
        </div>
        <PagePrimaryActionButton
          onClick={() => setFormModal({ open: true, mode: 'create', user: null })}
        >
          {page('tenant_users', 'new')}
        </PagePrimaryActionButton>
      </div>

      <div className="glass-card overflow-hidden">
        <div className="flex flex-wrap items-end gap-2 border-b border-border p-3">
          {isPlatformUser ? (
            <div className="min-w-[180px] shrink-0">
              <label htmlFor="tenant-users-tenant" className="block text-sm font-medium text-foreground mb-1">
                {col('tenant')}
              </label>
              <select
                id="tenant-users-tenant"
                value={selectedTenantId}
                onChange={e => {
                  setSelectedTenantId(e.target.value);
                  setPagination(prev => ({ ...prev, pageIndex: 0 }));
                }}
                className="block w-full h-9 px-3 py-1.5 text-sm bg-background border border-border rounded-md focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
              >
                <option value="">All tenant users (any tenant)</option>
                {tenants.map(t => (
                  <option key={t.id} value={t.id}>
                    {t.name ?? t.code ?? t.id}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <p className="min-w-[200px] shrink-0 pb-1 text-sm text-muted-foreground">
              Users in your organization
            </p>
          )}
          <SearchFilter
            id="tenant-users-search"
            label="Search"
            value={search}
            onChange={v => {
              setSearch(v);
              setPagination(prev => ({ ...prev, pageIndex: 0 }));
            }}
            placeholder={page('tenant_users', 'search_placeholder')}
            className="[&_input]:h-9 [&_input]:py-1.5 [&_input]:text-sm"
          />
          <StatusFilter
            id="tenant-users-status"
            label="Status"
            value={statusFilter}
            onChange={v => {
              setStatusFilter(v as TenantUsersStatusFilter);
              setPagination(prev => ({ ...prev, pageIndex: 0 }));
            }}
            options={statusOptions}
            emptyOption={allStatusesOption}
            className="[&_button]:h-9 [&_input]:h-9 [&_input]:py-1.5 [&_input]:text-sm"
          />
        </div>
        <div className="p-2">
          <TenantUsersTable
            rows={rows}
            controllers={controllers}
            rowOffset={rowOffset}
            onView={u => setFormModal({ open: true, mode: 'view', user: u })}
            onEdit={u => setFormModal({ open: true, mode: 'edit', user: u })}
            onDelete={handleDelete}
            onChangePassword={u => setChangePasswordModal({ open: true, user: u })}
            isDeleting={deleteMutation.isPending}
            isLoading={isLoading}
            emptyMessage={emptyMessage}
            showTenantColumn={showTenantColumn}
            tenantLabelById={tenantLabelById}
          />
        </div>
      </div>

      <TenantUserFormModal
        open={formModal.open}
        onClose={() => setFormModal(prev => ({ ...prev, open: false, user: null }))}
        mode={formModal.mode}
        user={formModal.user}
        tenants={tenants.map(t => ({ id: t.id ?? '', name: t.name, code: t.code }))}
        initialTenantId={selectedTenantId || null}
        onSuccess={() => setFormModal(prev => ({ ...prev, open: false, user: null }))}
      />
      <TenantUserChangePasswordModal
        open={changePasswordModal.open}
        onClose={() => setChangePasswordModal(prev => ({ ...prev, open: false, user: null }))}
        user={changePasswordModal.user}
        onSuccess={() => setChangePasswordModal(prev => ({ ...prev, open: false, user: null }))}
      />
    </div>
  );
}
